import { useEffect } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js"
import { trpc } from "@/lib/trpc-client"
import { versionsKey } from "@/lib/hooks/use-trip-data"
import { CLIENT_ID } from "@/lib/client-id"

export const guideKey = (guideId: string) => [`guide`, guideId] as const

export type GuideData = Awaited<
  ReturnType<typeof trpc.guides.getGuideData.query>
>

// Subscribe to the guide's Realtime Broadcast topic — the guide counterpart of
// useTripBroadcast. Statement-level Postgres triggers emit one `changed` signal
// per affected guide whenever a guide table is written (guide row or its
// days/items/places/notes/routes), carrying the originating client's id. We
// ignore our own echo — a local mutation already patched optimistically and
// invalidates itself — so the refetch only fires for changes that didn't
// originate in this tab: a server-side AI edit, a second tab, MCP, etc. The
// debounce coalesces the few signals a multi-statement transaction emits. The
// id is an opaque nonce and the refetch goes through authed tRPC, so the public
// channel leaks nothing. Without this, guides only reconciled on their own
// writes — AI edits (which commit server-side) stayed invisible until reload.
function useGuideBroadcast(guideId: string) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!guideId) return
    let timer: ReturnType<typeof setTimeout> | null = null

    const onChanged = (message: { payload?: { client_id?: string } }) => {
      if (message.payload?.client_id === CLIENT_ID) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: guideKey(guideId) })
        queryClient.invalidateQueries({ queryKey: versionsKey(`guide`, guideId) })
      }, 250)
    }

    // Lazy-import Supabase (see use-trip-data) so its ~203 KB bundle never lands
    // in the guide route's initial JS — realtime reconciliation is post-load.
    let channel: RealtimeChannel | undefined
    let client: SupabaseClient | undefined
    let removed = false
    void import(`@/lib/supabase-browser`).then(({ supabase }) => {
      if (removed) return
      client = supabase
      channel = supabase
        .channel(`guide:${guideId}`)
        .on(`broadcast`, { event: `changed` }, onChanged)
        .subscribe()
    })

    return () => {
      removed = true
      if (timer) clearTimeout(timer)
      if (client && channel) client.removeChannel(channel)
    }
  }, [guideId, queryClient])
}

// Owned-guide editing data via tRPC. Mirrors useTripData: a single aggregate
// query reconciled live by the guide's Broadcast subscription (above).
export function useGuideData(guideId: string) {
  useGuideBroadcast(guideId)

  return useQuery({
    queryKey: guideKey(guideId),
    queryFn: () => trpc.guides.getGuideData.query({ id: guideId }),
    enabled: !!guideId,
  })
}

export const guideSnapshotKey = (guideId: string) =>
  [`guide-snapshot`, guideId] as const

// Public / read-only guide view: fetch the published snapshot via tRPC (no
// Electric). Returns { snapshot: GuideSnapshot | null }. `enabled` gates it to
// read-only guide mode so owned/edit views don't fire it.
export function useGuideSnapshot(guideId: string, enabled: boolean) {
  return useQuery({
    queryKey: guideSnapshotKey(guideId),
    queryFn: () => trpc.guides.snapshot.query({ id: guideId }),
    enabled: enabled && !!guideId,
  })
}

export function useInvalidateGuideData() {
  const queryClient = useQueryClient()
  return (guideId: string) => {
    queryClient.invalidateQueries({ queryKey: guideKey(guideId) })
    // Keep the undo/redo history list in sync — mutations create new versions.
    queryClient.invalidateQueries({ queryKey: versionsKey(`guide`, guideId) })
  }
}

export function useGuideDataMutators(guideId: string) {
  const queryClient = useQueryClient()
  const optimisticUpdate = (updater: (data: GuideData) => GuideData) => {
    queryClient.setQueryData<GuideData>(guideKey(guideId), (old) =>
      old ? updater(old) : old
    )
  }
  return { optimisticUpdate }
}
